import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { PublicationListRequest, PublicationReadDetail } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import { ProjectContext } from "@/stores/project-provider";

import { createUpdateForQueries } from "./hooks/create-updater-for-queries";
import { queryDirectly } from "./hooks/query-directly";
import { QueryListReturn } from "./types/query-return";

// Single

export const getPublicationQuery = (publicationId: string | undefined) => {
  return getRanaApiV1().getQueryOptions<PublicationReadDetail>({
    queryKey: ["publication", "stat", publicationId],
    queryFn: async () => {
      if (!publicationId) throw Error("never happens");

      return getRanaApiV1().PublicationsApi.publicationRetrieve({
        publicationId,
      });
    },
    enabled: !!publicationId,
  });
};

export const usePublicationQuery = (publicationId: string | undefined) => {
  return useQuery(getPublicationQuery(publicationId));
};

export const usePublicationQueryDirectly = () => {
  const queryClient = useQueryClient();
  return async (publicationId: string) =>
    queryDirectly(queryClient, getPublicationQuery(publicationId));
};

// LIST

const getPublicationListQuery = (params: Omit<PublicationListRequest, "tenantId">) => {
  const { projectId, ...otherParams } = params;
  return getRanaApiV1().getQueryOptions({
    queryKey: ["publication", "list", projectId, otherParams],
    queryFn: async () => getRanaApiV1().PublicationsApi.publicationList(params),
  });
};

export const usePublicationListQuery = (
  params: Omit<PublicationListRequest, "projectId" | "tenantId">
) => {
  const { id: projectId } = useContext(ProjectContext);
  return useQuery(getPublicationListQuery({ projectId, orderBy: "-created_at", ...params }));
};

// INVALIDATION

export const useInvalidatePublicationListQuery = () => {
  const queryClient = useQueryClient();
  const { id: projectId } = useContext(ProjectContext);

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: getPublicationListQuery({ projectId }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};

export const useInvalidatePublicationQueries = () => {
  const queryClient = useQueryClient();
  const invalidatePublicationListQuery = useInvalidatePublicationListQuery();
  return async (publicationId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getPublicationQuery(publicationId).queryKey,
        refetchType: "all",
      }),
      invalidatePublicationListQuery(),
    ]);
  };
};

type PublicationQueryItem = QueryListReturn<typeof getPublicationListQuery>;

export const useUpdatePublicationQueries = () => {
  const { id: projectId } = useContext(ProjectContext);
  const queryClient = useQueryClient();

  return createUpdateForQueries<PublicationQueryItem>(queryClient, {
    getSingleQueryKey: (id) => getPublicationQuery(id).queryKey,
    getListQueryKey: () => getPublicationListQuery({ projectId }).queryKey.slice(0, -1),
  });
};
